import { useCallback, useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  KeyRound,
  CalendarDays,
  CheckCircle2,
  XCircle,
  FileSpreadsheet,
  Printer,
  Eye,
  MessageCircle,
  Search,
} from "lucide-react";
import {
  DataTable,
  PageTitle,
  MasterCrudPage,
  useRouteModulePermissions,
} from "@/components/common/Crud";
import { Combobox } from "@/components/common/Combobox";
import { DatePicker } from "@/components/common/DatePicker";
import { downloadCSV } from "@/lib/csv";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const STAFF_MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const STAFF_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatIsoDate(value) {
  return typeof value === "string" ? value.slice(0, 10) : "";
}

function formatDisplayDate(value) {
  const iso = formatIsoDate(value);
  if (!iso) return "-";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${day}-${month}-${year}`;
}

function normalizeLeaveType(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

function leaveTypeCode(value) {
  const normalized = normalizeLeaveType(value);
  if (!normalized) return "";
  if (normalized === "cl" || normalized.includes("casual")) return "CL";
  if (normalized === "sl" || normalized.includes("sick")) return "SL";
  if (normalized === "vl" || normalized.includes("vacation")) return "VL";
  if (normalized === "ml" || normalized.includes("maternity")) return "ML";
  if (normalized === "pl" || normalized.includes("paternity")) return "PL";
  if (normalized.includes("compoff") || normalized.includes("compensatory")) {
    return "comp_off";
  }
  return String(value || "").trim();
}

function leaveTypeMatches(row, typeLabel, leaveTypeId) {
  if (leaveTypeId && String(row.leave_type_id || "") === String(leaveTypeId)) {
    return true;
  }
  const rowCode = leaveTypeCode(row.type || "");
  const typeCode = leaveTypeCode(typeLabel || "");
  return rowCode !== "" && rowCode === typeCode;
}

function leaveFieldKeyForType(value) {
  const code = leaveTypeCode(value || "");
  const safeCode = code || normalizeLeaveType(value || "");
  return `leaves_${safeCode}`;
}

function resolveAttachmentUrl(path) {
  const value = String(path || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const normalized = value.startsWith("/") ? value : `/${value}`;
  return BACKEND_URL ? `${BACKEND_URL}${normalized}` : normalized;
}

function overlapsDate(date, startDate, endDate) {
  if (!date || !startDate) return false;
  const end = endDate || startDate;
  return startDate <= date && date <= end;
}

function overlapsMonth(monthStart, monthEnd, startDate, endDate) {
  if (!startDate) return false;
  const end = endDate || startDate;
  return startDate <= monthEnd && end >= monthStart;
}

function buildStaffMonthDays(year, month) {
  const totalDays = new Date(Number(year), Number(month), 0).getDate();
  return Array.from({ length: totalDays }, (_, index) => {
    const day = index + 1;
    const date = new Date(Number(year), Number(month) - 1, day);
    return {
      day,
      dateKey: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      weekdayLabel: STAFF_WEEKDAYS[date.getDay()],
      isSunday: date.getDay() === 0,
    };
  });
}

function normalizeHolidayRange(row) {
  const start = formatIsoDate(
    row.date || row.from_date || row.start_date || row.holiday_date || "",
  );
  const end = formatIsoDate(row.to_date || row.end_date || start);
  return { start, end };
}

function statusBadgeTone(status) {
  if (status === "approved")
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "rejected") return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

function staffDisplayName(row) {
  return (
    `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
    row.staff_id ||
    row.email ||
    "Staff"
  );
}

function canApproveLeave(role) {
  const normalized = String(role || "")
    .trim()
    .toLowerCase();
  return (
    normalized === "admin" ||
    normalized === "superadmin" ||
    normalized === "super_admin" ||
    normalized === "super-admin"
  );
}

function normalizedLeaveStatus(status) {
  return String(status || "pending")
    .trim()
    .toLowerCase();
}

function isOpenLeaveStatus(status) {
  const normalized = normalizedLeaveStatus(status);
  return normalized === "pending" || normalized === "approved";
}

function dateRangesOverlap(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false;
  return startA <= endB && startB <= endA;
}

function findStaffForUser(staffRows, user) {
  const email = String(user?.email || "")
    .trim()
    .toLowerCase();
  if (!email) return null;
  return (
    staffRows.find(
      (row) =>
        String(row.email || "")
          .trim()
          .toLowerCase() === email,
    ) || null
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildStaffRegisterExport(rows, monthDays) {
  const columns = [
    { key: "staff_id", label: "Staff ID" },
    { key: "full_name", label: "Staff" },
    { key: "role", label: "Role" },
    ...monthDays.map((day) => ({
      key: day.dateKey,
      label: `${String(day.day).padStart(2, "0")} ${day.weekdayLabel}`,
    })),
    { key: "present", label: "P" },
    { key: "absent", label: "A" },
    { key: "leave", label: "L" },
    { key: "half_day", label: "HD" },
    { key: "holiday", label: "H" },
    { key: "percentage", label: "%" },
  ];

  const exportRows = rows.map((staffRow) => {
    const row = {
      staff_id: staffRow.staff_id,
      full_name: staffRow.full_name,
      role: staffRow.role,
      present: staffRow.stats.P,
      absent: staffRow.stats.A,
      leave: staffRow.stats.L,
      half_day: staffRow.stats.HD,
      holiday: staffRow.stats.H,
      percentage: staffRow.percentage,
    };
    monthDays.forEach((day) => {
      row[day.dateKey] = staffRow.statusMap[day.dateKey] || "";
    });
    return row;
  });

  return { columns, rows: exportRows };
}

function buildStaffRegisterPrintHtml({ rows, monthDays, monthLabel, year }) {
  const headerCells = monthDays
    .map(
      (day) =>
        `<th class="${day.isSunday ? "sun" : ""}"><div>${String(day.day).padStart(2, "0")}</div><div class="weekday">${escapeHtml(day.weekdayLabel)}</div></th>`,
    )
    .join("");

  const bodyRows = rows
    .map((staffRow) => {
      const dayCells = monthDays
        .map((day) => {
          const value = staffRow.statusMap[day.dateKey] || "-";
          const tone =
            value === "P"
              ? "present"
              : value === "A"
                ? "absent"
                : value === "L"
                  ? "leave"
                  : value === "HD"
                    ? "half"
                    : value === "H"
                      ? "holiday"
                      : "empty";
          return `<td class="${day.isSunday ? "sun " : ""}${tone}">${escapeHtml(value)}</td>`;
        })
        .join("");

      return `<tr>
        <td class="staff-cell">
          <div class="staff-name">${escapeHtml(staffRow.full_name)}</div>
          <div class="staff-meta">${escapeHtml(staffRow.staff_id)} | ${escapeHtml(staffRow.role)}</div>
        </td>
        ${dayCells}
        <td class="summary present">${staffRow.stats.P}</td>
        <td class="summary absent">${staffRow.stats.A}</td>
        <td class="summary leave">${staffRow.stats.L}</td>
        <td class="summary half">${staffRow.stats.HD}</td>
        <td class="summary holiday">${staffRow.stats.H}</td>
        <td class="summary percent">${staffRow.percentage}%</td>
      </tr>`;
    })
    .join("");

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Staff Register ${escapeHtml(monthLabel)} ${escapeHtml(year)}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; }
        .head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 10px; }
        .title { font-size: 20px; font-weight: 700; margin: 0 0 4px; }
        .meta { font-size: 12px; color: #475569; }
        table { width: 100%; border-collapse: collapse; font-size: 10px; }
        th, td { border: 1px solid #cbd5e1; padding: 4px; text-align: center; }
        th { background: #f8fafc; font-weight: 700; }
        .weekday { font-size: 9px; color: #64748b; margin-top: 2px; }
        .staff-cell { text-align: left; min-width: 170px; }
        .staff-name { font-weight: 700; }
        .staff-meta { font-size: 9px; color: #64748b; margin-top: 2px; }
        .sun { background: #fff1f2; }
        .present { color: #15803d; font-weight: 700; }
        .absent { color: #be123c; font-weight: 700; }
        .leave { color: #b45309; font-weight: 700; }
        .half { color: #0369a1; font-weight: 700; }
        .holiday { color: #7c3aed; font-weight: 700; }
        .empty { color: #cbd5e1; }
        .summary { font-weight: 700; }
        .percent { background: #ecfdf5; }
      </style>
    </head>
    <body>
      <div class="head">
        <div>
          <div class="title">Monthly Staff Attendance Register</div>
          <div class="meta">Month: ${escapeHtml(monthLabel)} ${escapeHtml(year)}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Staff / Date</th>
            ${headerCells}
            <th>P</th>
            <th>A</th>
            <th>L</th>
            <th>HD</th>
            <th>H</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>${bodyRows}</tbody>
      </table>
      <script>window.onload = function(){ window.print(); };</script>
    </body>
  </html>`;
}

function SubNav({ items }) {
  return (
    <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs overflow-x-auto">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          data-testid={`staff-nav-${i.to.split("/").pop()}`}
          className={({ isActive }) =>
            `px-3 py-1.5 border-b-2 -mb-px whitespace-nowrap ${isActive ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`
          }
        >
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

function StaffField({
  form,
  setField,
  fieldKey,
  label,
  type,
  options,
  required,
}) {
  return (
    <div>
      <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
        {label}
      </Label>
      {options ? (
        <select
          className="h-9 w-full rounded-md border border-slate-300 text-xs px-2 bg-white"
          value={form[fieldKey] || ""}
          onChange={(e) => setField(fieldKey, e.target.value)}
          data-testid={`sf-${fieldKey}`}
          required={required}
        >
          <option value="">—</option>
          {options.map((option) => (
            <option key={option.value || option} value={option.value || option}>
              {option.label || option}
            </option>
          ))}
        </select>
      ) : type === "date" ? (
        <DatePicker
          value={form[fieldKey] || ""}
          onChange={(value) => setField(fieldKey, value)}
          className="h-9 text-xs"
          testId={`sf-${fieldKey}`}
        />
      ) : (
        <Input
          type={type || "text"}
          className="h-9 text-xs"
          value={form[fieldKey] || ""}
          onChange={(e) => setField(fieldKey, e.target.value)}
          data-testid={`sf-${fieldKey}`}
          required={required}
        />
      )}
    </div>
  );
}

function StaffActionDialog({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  onConfirm,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md border-slate-200">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl text-slate-900">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-6 text-slate-600">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* Staff Directory with 5-tab modal + reset password action */
function StaffDirectory() {
  const permissions = useRouteModulePermissions("staff", "directory");
  const { branchId, sessionId } = useAuth();
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [leaveTypeRows, setLeaveTypeRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [tab, setTab] = useState("basic");
  const [whatsappShare, setWhatsappShare] = useState(null);
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("");

  const load = async () => {
    const { data } = await api.get("/staff", {
      params: {
        branch_id: branchId,
        q: q || undefined,
        department_id: deptFilter || undefined,
      },
    });
    setRows(data.items || []);
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [branchId, q, deptFilter]);
  useEffect(() => {
    (async () => {
      const [d, ds, lt] = await Promise.all([
        api.get("/departments"),
        api.get("/designations"),
        api.get("/leave-types", {
          params: { branch_id: branchId, session_id: sessionId, limit: 300 },
        }),
      ]);
      setDepartments(d.data.items || []);
      setDesignations(ds.data.items || []);
      setLeaveTypeRows(lt.data.items || []);
    })();
  }, [branchId, sessionId]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      branch_id: branchId,
      session_id: sessionId,
      role: "teacher",
      status: "active",
    });
    setOpen(true);
    setTab("basic");
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...row,
      branch_id: row.branch_id || branchId,
      session_id: row.session_id || sessionId,
    });
    setOpen(true);
    setTab("basic");
  };

  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for staff directory");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for staff directory");
      return;
    }
    const firstName = String(form.first_name || "").trim();
    const email = String(form.email || "")
      .trim()
      .toLowerCase();
    const mobile = String(form.mobile || "").trim();
    const password = String(form.password || "").trim();

    if (!firstName || !email) {
      return toast.error("First name and email (username) are required");
    }
    if (!mobile) {
      return toast.error("Phone is required");
    }
    if (!editing && !password) {
      return toast.error("Login password is required");
    }

    const emailExists = rows.some(
      (row) =>
        row.id !== editing?.id &&
        String(row.email || "")
          .trim()
          .toLowerCase() === email,
    );
    if (emailExists) {
      return toast.error("Email (username) already exists");
    }

    const mobileExists = rows.some(
      (row) =>
        row.id !== editing?.id && String(row.mobile || "").trim() === mobile,
    );
    if (mobileExists) {
      return toast.error("Phone already exists");
    }

    try {
      const payload = {
        ...form,
        first_name: firstName,
        email,
        mobile,
        branch_id: form.branch_id || branchId,
        session_id: form.session_id || sessionId,
      };
      if (editing) {
        await api.put(`/staff/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/staff", payload);
        toast.success("Staff created with login");
        setWhatsappShare({
          name: `${firstName} ${form.last_name || ""}`.trim(),
          mobile,
          email,
          password,
        });
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const shareWelcomeOnWhatsapp = async () => {
    if (!whatsappShare) return;
    const { name, mobile, email, password } = whatsappShare;
    let schoolName = "our school";
    try {
      const cfg = await api.get("/settings/school_info", {
        params: { branch_id: branchId || undefined },
      });
      schoolName =
        cfg?.data?.value?.school_name || cfg?.data?.school_name || schoolName;
    } catch {
      // keep fallback name
    }
    const lines = [
      `Welcome to ${schoolName}! `,
      ``,
      `Hi ${name}, your staff login has been created.`,
      ``,
      `Login Email: ${email}`,
      password ? `Password: ${password}` : null,
      ``,
      `Login here: ${window.location.origin}`,
      ``,
      `Please change your password after your first login.`,
    ].filter((line) => line !== null);
    const digits = String(mobile || "").replace(/\D/g, "");
    const phone = digits.length === 10 ? `91${digits}` : digits;
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setWhatsappShare(null);
  };

  const openDelete = (row) => setDeleteRow(row);

  const del = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for staff directory");
      return;
    }
    await api.delete(`/staff/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  const openResetPw = async (row) => {
    if (!row.email) {
      toast.error("No email on file");
      return;
    }
    const staffEmail = String(row.email || "")
      .trim()
      .toLowerCase();
    setPwUser({ id: row.user_id || null, email: staffEmail });
    setNewPw("");
    setPwOpen(true);
  };

  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Min 6 chars");
    await api.post("/auth/admin/reset-password", {
      user_id: pwUser?.id || "",
      email: pwUser?.email || "",
      new_password: newPw,
    });
    toast.success("Password reset");
    setPwOpen(false);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="staff-directory">
      <PageTitle
        title="Staff Directory"
        subtitle="Auto-creates login when staff is created · Admin can reset password"
        actions={
          permissions.canAdd ? (
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              data-testid="add-staff"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Staff
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search name / email / mobile…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-8 pl-7 w-48 sm:w-64 text-xs"
            data-testid="search-staff"
          />
        </div>
        <select
          className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          data-testid="filter-department"
        >
          <option value="">All Departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        testid="staff-table"
        columns={[
          {
            key: "sno",
            label: "S.No.",
            render: (_, idx) => idx + 1,
          },
          {
            key: "employee_code",
            label: "Emp Code",
            render: (r) => r.employee_code || <span className="text-slate-300">—</span>,
          },
          {
            key: "name",
            label: "Name",
            render: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim(),
          },
          { key: "role", label: "Role" },
          {
            key: "designation_id",
            label: "Designation",
            render: (r) =>
              designations.find((d) => d.id === r.designation_id)?.name || "—",
          },
          {
            key: "department_id",
            label: "Department",
            render: (r) =>
              departments.find((d) => d.id === r.department_id)?.name || "—",
          },
          { key: "email", label: "Email" },
          { key: "mobile", label: "Mobile" },
          {
            key: "pw",
            label: "Login",
            render: (r) => (
              <button
                onClick={() => openResetPw(r)}
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-semibold flex items-center gap-1"
                data-testid={`reset-${r.id}`}
              >
                <KeyRound className="w-3 h-3" /> Reset PW
              </button>
            ),
          },
        ]}
        rows={rows}
        onEdit={permissions.canEdit ? openEdit : undefined}
        onDelete={permissions.canDelete ? openDelete : undefined}
      />

      {/* Edit/Add staff dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Staff" : "Add New Staff"}
            </DialogTitle>
          </DialogHeader>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="payroll">Payroll</TabsTrigger>
              <TabsTrigger value="leaves">Leaves</TabsTrigger>
              <TabsTrigger value="bank">Bank</TabsTrigger>
              <TabsTrigger value="medical">Medical</TabsTrigger>
            </TabsList>
            <TabsContent
              value="basic"
              className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3"
            >
              <StaffField
                form={form}
                setField={set}
                fieldKey="role"
                label="Role *"
                options={[
                  { value: "teacher", label: "Teacher" },
                  { value: "accountant", label: "Accountant" },
                  { value: "librarian", label: "Librarian" },
                  { value: "receptionist", label: "Receptionist" },
                  { value: "principal", label: "Principal" },
                  { value: "admin", label: "Admin" },
                ]}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="designation_id"
                label="Designation"
                options={designations.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="department_id"
                label="Department"
                options={departments.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="employee_code"
                label="Employee Code"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="gender"
                label="Gender"
                options={["Male", "Female", "Other"]}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="first_name"
                label="First Name *"
                required
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="last_name"
                label="Last Name"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="marital_status"
                label="Marital Status"
                options={["Single", "Married", "Divorced", "Widowed"]}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey={
                  form.gender === "Female" && form.marital_status === "Married"
                    ? "husband_name"
                    : "father_name"
                }
                label={
                  form.gender === "Female" && form.marital_status === "Married"
                    ? "Husband Name"
                    : "Father Name"
                }
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="email"
                label="Email (Username) *"
                type="email"
                required
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="password"
                label="Login Password *"
                type="password"
                required={!editing}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="dob"
                label="DOB"
                type="date"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="joining_date"
                label="Date of Joining"
                type="date"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="mobile"
                label="Phone *"
                required
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="blood_group"
                label="Blood Group"
                options={["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"]}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="teacher_type"
                label="Teacher Type"
                options={["PRT", "TGT", "PGT"]}
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="aadhaar_no"
                label="Aadhar Card"
              />
            </TabsContent>
            <TabsContent
              value="payroll"
              className="mt-3 grid grid-cols-3 gap-3"
            >
              <StaffField
                form={form}
                setField={set}
                fieldKey="epf_no"
                label="EPF No"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="salary"
                label="Basic Salary"
                type="number"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="contract_type"
                label="Contract Type"
                options={["Permanent", "Contract", "Probation"]}
              />
            </TabsContent>
            <TabsContent value="leaves" className="mt-3 grid grid-cols-3 gap-3">
              {leaveTypeRows.map((typeRow) => {
                const typeName = typeRow.name || typeRow.type || "";
                const fieldKey = leaveFieldKeyForType(typeName);
                if (!fieldKey || !typeName) return null;
                return (
                  <StaffField
                    key={typeRow.id || typeName}
                    form={form}
                    setField={set}
                    fieldKey={fieldKey}
                    label={typeName}
                    type="number"
                  />
                );
              })}
            </TabsContent>
            <TabsContent value="bank" className="mt-3 grid grid-cols-2 gap-3">
              <StaffField
                form={form}
                setField={set}
                fieldKey="account_title"
                label="Account Title"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="bank_account_no"
                label="Account Number"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="bank_name"
                label="Bank Name"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="bank_ifsc"
                label="IFSC Code"
              />
            </TabsContent>
            <TabsContent
              value="medical"
              className="mt-3 grid grid-cols-2 gap-3"
            >
              <StaffField
                form={form}
                setField={set}
                fieldKey="allergy"
                label="Allergy"
              />
              <StaffField
                form={form}
                setField={set}
                fieldKey="medical_history"
                label="Medical History"
              />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} className="erp-primary">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div>
            <div className="text-xs text-slate-500 mb-2">
              User:{" "}
              <span className="font-mono erp-text-primary">
                {pwUser?.email}
              </span>
            </div>
            <Label className="text-xs font-semibold">New Password</Label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="h-9"
              data-testid="reset-newpw"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={resetPw}
              className="erp-primary"
              data-testid="reset-confirm"
            >
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffActionDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
        title={`Delete ${deleteRow?.first_name || "staff"}?`}
        description="This will permanently remove staff profile and related staff record data for this user."
        actionLabel="Delete Staff"
        onConfirm={del}
      />

      <AlertDialog
        open={!!whatsappShare}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setWhatsappShare(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              Share welcome message?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              Send {whatsappShare?.name || "this staff member"} a welcome
              message with their login email and password on WhatsApp (
              {whatsappShare?.mobile || "no number"}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setWhatsappShare(null)}>
              Not now
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={shareWelcomeOnWhatsapp}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Share on WhatsApp
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* Staff Attendance mark page */
function StaffAttendance() {
  const permissions = useRouteModulePermissions("staff", "attendance");
  const { branchId, sessionId } = useAuth();
  const [mode, setMode] = useState("daily");
  const [staff, setStaff] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState({});
  const [existingRows, setExistingRows] = useState({});
  const [month, setMonth] = useState(
    String(new Date().getMonth() + 1).padStart(2, "0"),
  );
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [registerRows, setRegisterRows] = useState([]);
  const [registerBusy, setRegisterBusy] = useState(false);

  const load = useCallback(async () => {
    const [staffRes, attendanceRes, leaveRes, holidayRes] = await Promise.all([
      api.get("/staff", {
        params: { branch_id: branchId, session_id: sessionId, limit: 500 },
      }),
      api.get("/staff-attendance", {
        params: {
          branch_id: branchId,
          session_id: sessionId,
          date,
          limit: 1000,
        },
      }),
      api.get("/leave-requests", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      }),
      api.get("/holidays", {
        params: { branch_id: branchId, session_id: sessionId, limit: 500 },
      }),
    ]);

    const staffRows = staffRes.data.items || [];
    const attendanceRows = attendanceRes.data.items || [];
    const leaveRows = (leaveRes.data.items || []).filter(
      (row) =>
        row.status === "approved" &&
        overlapsDate(
          date,
          formatIsoDate(row.from_date),
          formatIsoDate(row.to_date),
        ),
    );
    const holidayRows = (holidayRes.data.items || []).filter((row) => {
      const range = normalizeHolidayRange(row);
      return overlapsDate(date, range.start, range.end);
    });

    setStaff(staffRows);
    const attendanceMap = {};
    const idMap = {};
    const holidayActive = holidayRows.length > 0;
    const leaveByStaff = new Set(
      leaveRows.map((row) => String(row.staff_id || "")),
    );

    staffRows.forEach((row) => {
      attendanceMap[row.id] = leaveByStaff.has(String(row.id))
        ? "L"
        : holidayActive
          ? "H"
          : "P";
    });
    attendanceRows.forEach((row) => {
      if (!row.staff_id) return;
      attendanceMap[row.staff_id] = row.status || "P";
      idMap[row.staff_id] = row.id;
    });
    setStatus(attendanceMap);
    setExistingRows(idMap);
  }, [branchId, date, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRegister = useCallback(async () => {
    setRegisterBusy(true);
    try {
      const monthStart = `${year}-${month}-01`;
      const monthEnd = `${year}-${month}-${String(new Date(Number(year), Number(month), 0).getDate()).padStart(2, "0")}`;
      const [staffRes, attendanceRes, leaveRes, holidayRes] = await Promise.all(
        [
          api.get("/staff", {
            params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
          }),
          api.get("/staff-attendance", {
            params: { branch_id: branchId, session_id: sessionId, limit: 5000 },
          }),
          api.get("/leave-requests", {
            params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
          }),
          api.get("/holidays", {
            params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
          }),
        ],
      );

      const monthKey = `${year}-${month}`;
      const monthDays = buildStaffMonthDays(year, month);
      const staffRows = staffRes.data.items || [];
      const attendanceRows = (attendanceRes.data.items || []).filter((row) =>
        formatIsoDate(row.date).startsWith(monthKey),
      );
      const leaveRows = (leaveRes.data.items || []).filter(
        (row) =>
          row.status === "approved" &&
          overlapsMonth(
            monthStart,
            monthEnd,
            formatIsoDate(row.from_date),
            formatIsoDate(row.to_date),
          ),
      );
      const holidayRows = (holidayRes.data.items || []).filter((row) => {
        const range = normalizeHolidayRange(row);
        return overlapsMonth(monthStart, monthEnd, range.start, range.end);
      });

      const registerMap = new Map();
      staffRows.forEach((row) => {
        registerMap.set(String(row.id), {
          id: String(row.id),
          staff_id: row.staff_id || row.staff_no || "-",
          full_name: staffDisplayName(row),
          role: row.role || "-",
          statusMap: {},
        });
      });

      attendanceRows.forEach((row) => {
        const key = String(row.staff_id || "");
        if (!registerMap.has(key) || !row.date) return;
        registerMap.get(key).statusMap[formatIsoDate(row.date)] =
          row.status || "P";
      });

      leaveRows.forEach((row) => {
        const key = String(row.staff_id || "");
        const entry = registerMap.get(key);
        if (!entry) return;
        monthDays.forEach((day) => {
          if (
            !entry.statusMap[day.dateKey] &&
            overlapsDate(
              day.dateKey,
              formatIsoDate(row.from_date),
              formatIsoDate(row.to_date),
            )
          ) {
            entry.statusMap[day.dateKey] = "L";
          }
        });
      });

      holidayRows.forEach((row) => {
        const range = normalizeHolidayRange(row);
        monthDays.forEach((day) => {
          if (!overlapsDate(day.dateKey, range.start, range.end)) return;
          registerMap.forEach((entry) => {
            if (!entry.statusMap[day.dateKey]) {
              entry.statusMap[day.dateKey] = "H";
            }
          });
        });
      });

      setRegisterRows(
        Array.from(registerMap.values()).map((entry) => {
          const stats = { P: 0, A: 0, L: 0, HD: 0, H: 0 };
          monthDays.forEach((day) => {
            const value = entry.statusMap[day.dateKey];
            if (stats[value] !== undefined) stats[value] += 1;
          });
          const workingDays = monthDays.length - stats.H;
          const presentBase = stats.P + stats.L + stats.HD;
          return {
            ...entry,
            stats,
            percentage:
              workingDays > 0
                ? Math.round((presentBase * 100) / workingDays)
                : 0,
          };
        }),
      );
    } finally {
      setRegisterBusy(false);
    }
  }, [branchId, month, sessionId, year]);

  useEffect(() => {
    if (mode === "register") {
      loadRegister();
    }
  }, [loadRegister, mode]);

  const save = async () => {
    if (!permissions.canAdd && !permissions.canEdit) {
      toast.error("You do not have permission to save staff attendance");
      return;
    }
    await Promise.all(
      staff.map((row) => {
        const payload = {
          staff_id: row.id,
          date,
          status: status[row.id] || "P",
          branch_id: branchId,
          session_id: sessionId,
        };
        if (existingRows[row.id]) {
          return api.put(`/staff-attendance/${existingRows[row.id]}`, payload);
        }
        return api.post("/staff-attendance", payload);
      }),
    );
    toast.success("Saved");
    load();
  };

  const monthDays = buildStaffMonthDays(year, month);
  const yearOptions = Array.from({ length: 5 }, (_, index) =>
    String(new Date().getFullYear() - 2 + index),
  );
  const monthLabel =
    STAFF_MONTH_OPTIONS.find((item) => item.value === month)?.label || month;

  const setAll = (value) => {
    const next = {};
    staff.forEach((row) => {
      next[row.id] = value;
    });
    setStatus(next);
  };

  const exportRegisterExcel = () => {
    if (registerRows.length === 0) {
      toast.info("No register rows to export");
      return;
    }
    const exportData = buildStaffRegisterExport(registerRows, monthDays);
    downloadCSV(
      exportData.rows,
      exportData.columns,
      `staff-register-${year}-${month}.csv`,
    );
  };

  const exportRegisterPdf = () => {
    if (registerRows.length === 0) {
      toast.info("No register rows to export");
      return;
    }
    const popup = window.open("", "_blank", "width=1400,height=900");
    if (!popup) {
      toast.error("Popup blocked. Allow popups to export PDF.");
      return;
    }
    popup.document.write(
      buildStaffRegisterPrintHtml({
        rows: registerRows,
        monthDays,
        monthLabel,
        year,
      }),
    );
    popup.document.close();
    popup.focus();
  };

  return (
    <div data-testid="staff-attendance">
      <PageTitle
        title="Staff Attendance"
        subtitle={
          mode === "daily"
            ? "Mark daily staff attendance"
            : "Monthly staff register with holidays and approved leaves"
        }
      />
      <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={() => setMode("daily")}
          className={`rounded-md px-3 py-2 text-sm font-semibold transition ${mode === "daily" ? "bg-[var(--erp-primary)] text-white" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Daily Attendance
        </button>
        <button
          type="button"
          onClick={() => setMode("register")}
          className={`rounded-md px-3 py-2 text-sm font-semibold transition ${mode === "register" ? "bg-[var(--erp-primary)] text-white" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Staff Register
        </button>
      </div>

      {mode === "daily" ? (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <DatePicker
              value={date}
              onChange={setDate}
              className="h-8 w-40 text-xs"
              testId="sa-date"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAll("P")}
              className="h-8"
            >
              All Present
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAll("A")}
              className="h-8"
            >
              All Absent
            </Button>
            <Button
              size="sm"
              onClick={save}
              className="h-8 erp-primary"
              data-testid="sa-save"
            >
              Save
            </Button>
          </div>
          <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-slate-100 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-3 py-1.5">{staffDisplayName(row)}</td>
                    <td className="px-3 py-1.5">{row.role}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex gap-1 flex-wrap">
                        {["P", "A", "L", "HD", "H"].map((value) => (
                          <button
                            key={value}
                            onClick={() =>
                              setStatus((prev) => ({
                                ...prev,
                                [row.id]: value,
                              }))
                            }
                            data-testid={`sa-${row.id}-${value}`}
                            className={`px-2 py-0.5 rounded text-[11px] font-bold border ${status[row.id] === value ? (value === "P" ? "bg-emerald-600 text-white border-emerald-600" : value === "A" ? "bg-red-600 text-white border-red-600" : value === "L" ? "bg-amber-600 text-white border-amber-600" : value === "H" ? "bg-violet-600 text-white border-violet-600" : "bg-blue-600 text-white border-blue-600") : "bg-white border-slate-300 text-slate-600"}`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Month
                </Label>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm"
                >
                  {STAFF_MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Year
                </Label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm"
                >
                  {yearOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                className="h-9 erp-primary"
                onClick={loadRegister}
                disabled={registerBusy}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                {registerBusy ? "Loading..." : "Load Register"}
              </Button>
              <div className="ml-auto flex items-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 min-w-[104px]"
                  onClick={exportRegisterExcel}
                  disabled={registerRows.length === 0}
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 min-w-[96px]"
                  onClick={exportRegisterPdf}
                  disabled={registerRows.length === 0}
                >
                  <Printer className="mr-2 h-4 w-4" /> PDF
                </Button>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="sticky left-0 z-20 min-w-[200px] border-b border-slate-200 bg-slate-100 px-3 py-2 text-left text-[11px] font-bold uppercase">
                      Staff / Date
                    </th>
                    {monthDays.map((day) => (
                      <th
                        key={day.dateKey}
                        className={`min-w-[44px] border-b border-slate-200 px-1.5 py-1.5 text-center text-[11px] font-bold ${day.isSunday ? "bg-rose-50" : "bg-slate-100"}`}
                      >
                        <div>{String(day.day).padStart(2, "0")}</div>
                        <div className="mt-0.5 text-[10px] text-slate-500">
                          {day.weekdayLabel}
                        </div>
                      </th>
                    ))}
                    {["P", "A", "L", "HD", "H", "%"].map((label) => (
                      <th
                        key={label}
                        className="min-w-[40px] border-b border-slate-200 bg-slate-100 px-1.5 py-1.5 text-center text-[11px] font-bold uppercase"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {registerRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 hover:bg-slate-50/70"
                    >
                      <td className="sticky left-0 z-10 border-r border-slate-100 bg-white px-3 py-2 align-top">
                        <div className="font-semibold text-slate-900">
                          {row.full_name}
                        </div>
                        <div className="mt-1 text-[11px] font-mono text-slate-500">
                          {row.staff_id} · {row.role}
                        </div>
                      </td>
                      {monthDays.map((day) => {
                        const value = row.statusMap[day.dateKey] || "-";
                        const tone =
                          value === "P"
                            ? "text-emerald-700"
                            : value === "A"
                              ? "text-rose-700"
                              : value === "L"
                                ? "text-amber-700"
                                : value === "H"
                                  ? "text-violet-700"
                                  : value === "HD"
                                    ? "text-sky-700"
                                    : "text-slate-300";
                        return (
                          <td
                            key={day.dateKey}
                            className={`px-1.5 py-2 text-center text-xs font-bold ${day.isSunday ? "bg-rose-50/60" : ""} ${tone}`}
                          >
                            {value}
                          </td>
                        );
                      })}
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-emerald-700">
                        {row.stats.P}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-rose-700">
                        {row.stats.A}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-amber-700">
                        {row.stats.L}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-sky-700">
                        {row.stats.HD}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-violet-700">
                        {row.stats.H}
                      </td>
                      <td className="px-1.5 py-2 text-center">
                        <span className="inline-flex min-w-[34px] justify-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
                          {row.percentage}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StaffPayrollPage() {
  const permissions = useRouteModulePermissions("staff", "payroll");
  const { branchId, sessionId } = useAuth();
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [monthFilter, setMonthFilter] = useState("");
  const [form, setForm] = useState({
    month: new Date().toISOString().slice(0, 7),
    basic: "",
    allowances: "",
    deductions: "",
    net: "",
    status: "draft",
    mode: "",
    paid_on: "",
  });

  const numberValue = (value) => {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const load = useCallback(async () => {
    const [staffRes, payrollRes] = await Promise.all([
      api.get("/staff", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      }),
      api.get("/payrolls", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      }),
    ]);
    setStaff(staffRes.data.items || []);
    setRows(payrollRes.data.items || []);
  }, [branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const setComputedNet = (next) => {
    const basic = numberValue(next.basic);
    const allowances = numberValue(next.allowances);
    const deductions = numberValue(next.deductions);
    const gross = basic + allowances;
    const net = gross - deductions;
    return {
      ...next,
      net: net.toFixed(2),
    };
  };

  const openCreate = () => {
    setEditing(null);
    const fresh = {
      month: new Date().toISOString().slice(0, 7),
      basic: "",
      allowances: "",
      deductions: "",
      net: "0.00",
      status: "draft",
      mode: "",
      paid_on: "",
      branch_id: branchId,
      session_id: sessionId,
    };
    setForm(fresh);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const gross = numberValue(row.gross);
    const deductions = numberValue(row.deductions);
    const net = row.net ?? gross - deductions;
    const editForm = {
      ...row,
      basic: String(gross),
      allowances: "0",
      deductions: String(deductions),
      net: String(net),
      status: row.status || (row.paid_on ? "paid" : "draft"),
      branch_id: row.branch_id || branchId,
      session_id: row.session_id || sessionId,
    };
    setForm(editForm);
    setOpen(true);
  };

  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for payroll");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for payroll");
      return;
    }
    if (!form.staff_id || !form.month) {
      toast.error("Staff and month are required");
      return;
    }
    const basic = numberValue(form.basic);
    const allowances = numberValue(form.allowances);
    const deductions = numberValue(form.deductions);
    const gross = basic + allowances;
    const net = gross - deductions;

    const payload = {
      ...form,
      gross,
      deductions,
      net,
      status: form.status || "draft",
      branch_id: form.branch_id || branchId,
      session_id: form.session_id || sessionId,
    };

    if (editing) await api.put(`/payrolls/${editing.id}`, payload);
    else await api.post("/payrolls", payload);
    toast.success(editing ? "Payroll updated" : "Payroll created");
    setOpen(false);
    load();
  };

  const openDelete = (row) => setDeleteRow(row);

  const remove = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for payroll");
      return;
    }
    await api.delete(`/payrolls/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "basic" || key === "allowances" || key === "deductions") {
        return setComputedNet(next);
      }
      return next;
    });
  };

  const staffNameById = (staffRows, id) =>
    staffDisplayName(
      staffRows.find((row) => String(row.id) === String(id)) || {},
    );
  const payrollStaffOptions = staff.map((row) => ({
    value: String(row.id),
    label: staffDisplayName(row),
    sub: row.staff_id || row.email || "",
  }));

  const filteredRows = rows.filter((row) =>
    monthFilter ? String(row.month || "").startsWith(monthFilter) : true,
  );

  return (
    <div data-testid="staff-payroll-page">
      <PageTitle
        title="Payroll"
        subtitle="Generate salary month-wise with computed net pay"
        actions={
          <div className="flex gap-2">
            <Input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="h-8 w-40 text-xs"
            />
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              disabled={!permissions.canAdd}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Payroll
            </Button>
          </div>
        }
      />
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-slate-100 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Staff</th>
              <th className="px-3 py-2 text-left">Month</th>
              <th className="px-3 py-2 text-left">Gross</th>
              <th className="px-3 py-2 text-left">Deductions</th>
              <th className="px-3 py-2 text-left">Net</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-slate-100 hover:bg-slate-50"
              >
                <td className="px-3 py-2">
                  {staffNameById(staff, row.staff_id)}
                </td>
                <td className="px-3 py-2">{row.month || "-"}</td>
                <td className="px-3 py-2">{row.gross ?? "0"}</td>
                <td className="px-3 py-2">{row.deductions ?? "0"}</td>
                <td className="px-3 py-2 font-semibold">{row.net ?? "0"}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-semibold ${
                      row.status === "paid"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : row.status === "approved"
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    {row.status || (row.paid_on ? "paid" : "draft")}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      onClick={() => openEdit(row)}
                      disabled={!permissions.canEdit}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      onClick={() => openDelete(row)}
                      disabled={!permissions.canDelete}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Payroll" : "Add Payroll"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Staff
              </Label>
              <Combobox
                value={form.staff_id || ""}
                onChange={(value) => setField("staff_id", value)}
                options={payrollStaffOptions}
                placeholder="Select staff"
                searchPlaceholder="Search staff by name or staff id"
                testid="staff-payroll-select"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Month
              </Label>
              <Input
                type="month"
                value={form.month || ""}
                onChange={(e) => setField("month", e.target.value)}
                className="h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Basic
              </Label>
              <Input
                type="number"
                value={form.basic || ""}
                onChange={(e) => setField("basic", e.target.value)}
                className="h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Allowances
              </Label>
              <Input
                type="number"
                value={form.allowances || ""}
                onChange={(e) => setField("allowances", e.target.value)}
                className="h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Deductions
              </Label>
              <Input
                type="number"
                value={form.deductions || ""}
                onChange={(e) => setField("deductions", e.target.value)}
                className="h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Net Pay
              </Label>
              <Input
                value={form.net || "0.00"}
                readOnly
                className="h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Status
              </Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-xs px-2 bg-white"
                value={form.status || "draft"}
                onChange={(e) => setField("status", e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Paid On
              </Label>
              <DatePicker
                value={form.paid_on || ""}
                onChange={(value) => setField("paid_on", value)}
                className="h-9 text-xs"
                testId="staff-payroll-paid-on"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Payment Mode
              </Label>
              <Input
                value={form.mode || ""}
                onChange={(e) => setField("mode", e.target.value)}
                className="h-9 text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} className="erp-primary">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffActionDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
        title="Delete payroll record?"
        description="This will remove the selected payroll entry for the selected month and staff member."
        actionLabel="Delete Payroll"
        onConfirm={remove}
      />
    </div>
  );
}

function StaffLeavePage() {
  const permissions = useRouteModulePermissions("staff", "leave_requests");
  const { branchId, sessionId, user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterStaffId, setFilterStaffId] = useState("");
  const [leaveTypeRows, setLeaveTypeRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ type: "", status: "pending" });
  const canApprove = canApproveLeave(user?.role);
  const currentStaff = findStaffForUser(staff, user);

  const load = useCallback(async () => {
    const [staffRes, leaveRes, leaveTypeRes] = await Promise.all([
      api.get("/staff", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      }),
      api.get("/leave-requests", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      }),
      api.get("/leave-types", {
        params: { branch_id: branchId, session_id: sessionId, limit: 300 },
      }),
    ]);
    setStaff(staffRes.data.items || []);
    setRows(leaveRes.data.items || []);
    setLeaveTypeRows(leaveTypeRes.data.items || []);
  }, [branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    const selectedStaffId = !canApprove && currentStaff ? currentStaff.id : "";
    const firstType = leaveTypeRows[0];
    setForm({
      staff_id: selectedStaffId,
      type: firstType?.name || "",
      leave_type_id: firstType?.id || "",
      status: "pending",
      branch_id: branchId,
      session_id: sessionId,
    });
    setAttachmentFile(null);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const matchedType =
      leaveTypeRows.find(
        (typeRow) => String(typeRow.id) === String(row.leave_type_id),
      ) ||
      leaveTypeRows.find(
        (typeRow) => leaveTypeCode(typeRow.name) === leaveTypeCode(row.type),
      );
    setForm({
      ...row,
      leave_type_id: row.leave_type_id || matchedType?.id || "",
      type: row.type || matchedType?.name || "",
    });
    setAttachmentFile(null);
    setOpen(true);
  };

  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for leave requests");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for leave requests");
      return;
    }
    if (!form.staff_id || !form.from_date || !form.to_date) {
      toast.error("Staff, from date and to date required");
      return;
    }
    if (form.to_date < form.from_date) {
      toast.error("To date must be greater than or equal to from date");
      return;
    }
    if (!form.leave_type_id) {
      toast.error("Select leave type");
      return;
    }
    if (!canApprove) {
      if (!currentStaff) {
        toast.error("Your login is not mapped to a staff profile");
        return;
      }
      if (String(form.staff_id) !== String(currentStaff.id)) {
        toast.error("You can only apply leave for your own profile");
        return;
      }
    }

    const activeOverlap = rows.find((row) => {
      if (editing && String(row.id) === String(editing.id)) return false;
      if (String(row.staff_id) !== String(form.staff_id)) return false;
      if (!isOpenLeaveStatus(row.status)) return false;
      return dateRangesOverlap(
        formatIsoDate(form.from_date),
        formatIsoDate(form.to_date),
        formatIsoDate(row.from_date),
        formatIsoDate(row.to_date),
      );
    });
    if (activeOverlap) {
      toast.error("Existing pending or approved leave overlaps selected dates");
      return;
    }

    if (editing && String(editing.status || "pending") !== "pending") {
      toast.error("Only pending leave requests can be edited");
      return;
    }

    const selectedLeaveType = leaveTypeRows.find(
      (typeRow) => String(typeRow.id) === String(form.leave_type_id || ""),
    );

    const payload = {
      ...form,
      leave_type_id: selectedLeaveType?.id || form.leave_type_id || null,
      type: selectedLeaveType?.name || form.type || "",
      branch_id: form.branch_id || branchId,
      session_id: form.session_id || sessionId,
      status: editing ? form.status || "pending" : "pending",
    };

    if (attachmentFile) {
      try {
        setUploading(true);
        const body = new FormData();
        body.append("file", attachmentFile);
        const uploadRes = await api.post("/upload?tag=leave", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        payload.attachment_url = uploadRes.data?.url || "";
        payload.attachment_name =
          uploadRes.data?.filename || attachmentFile.name;
      } finally {
        setUploading(false);
      }
    }

    if (editing) await api.put(`/leave-requests/${editing.id}`, payload);
    else await api.post("/leave-requests", payload);
    toast.success(editing ? "Leave updated" : "Leave applied");
    setOpen(false);
    load();
  };

  const openDelete = (row) => setDeleteRow(row);

  const remove = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for leave requests");
      return;
    }
    await api.delete(`/leave-requests/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  const staffNameById = (id) =>
    staffDisplayName(staff.find((row) => String(row.id) === String(id)) || {});
  const selectableStaff = !canApprove && currentStaff ? [currentStaff] : staff;
  const leaveStaffOptions = selectableStaff.map((row) => ({
    value: String(row.id),
    label: staffDisplayName(row),
    sub: row.staff_id || row.email || "",
  }));
  const leaveFilterStaffOptions = [
    { value: "", label: "All Staff", sub: "" },
    ...leaveStaffOptions,
  ];
  const leaveTypeOptions = leaveTypeRows;
  const leaveTypeSelectOptions = leaveTypeOptions.map((typeRow) => ({
    value: String(typeRow.id),
    label: typeRow.name || "",
    sub: "",
  }));
  const selectedLeaveType = leaveTypeOptions.find(
    (typeRow) => String(typeRow.id) === String(form.leave_type_id || ""),
  );
  const selectedTypeLabel = selectedLeaveType?.name || form.type || "";
  const selectedTypeCode = leaveTypeCode(selectedTypeLabel);
  const selectedPendingLeaves = rows.filter((row) => {
    if (editing && String(row.id) === String(editing.id)) return false;
    if (String(row.staff_id || "") !== String(form.staff_id || ""))
      return false;
    if (normalizedLeaveStatus(row.status) !== "pending") return false;
    if (!selectedTypeCode) return true;
    return leaveTypeCode(row.type || "") === selectedTypeCode;
  });

  const filteredRows = rows.filter((row) => {
    if (filterStaffId && String(row.staff_id || "") !== String(filterStaffId)) {
      return false;
    }
    const rowFrom = formatIsoDate(row.from_date);
    const rowTo = formatIsoDate(row.to_date || row.from_date);
    if (filterFromDate && rowTo < filterFromDate) return false;
    if (filterToDate && rowFrom > filterToDate) return false;
    return true;
  });

  const exportLeaveExcel = () => {
    if (filteredRows.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    const columns = [
      { key: "staff", label: "Staff" },
      { key: "type", label: "Type" },
      { key: "applied_on", label: "Applied On" },
      { key: "from", label: "From" },
      { key: "to", label: "To" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status" },
      { key: "attachment", label: "Attachment" },
    ];
    const exportRows = filteredRows.map((row) => ({
      staff: staffNameById(row.staff_id),
      type: row.type || "-",
      applied_on: formatDisplayDate(row.created_at),
      from: formatDisplayDate(row.from_date),
      to: formatDisplayDate(row.to_date),
      reason: row.reason || "",
      status: row.status || "pending",
      attachment: row.attachment_name || row.attachment_url || "",
    }));
    downloadCSV(exportRows, columns, "staff_apply_leave.csv");
    toast.success(`Exported ${exportRows.length} rows`);
  };

  return (
    <div data-testid="staff-leave-page">
      <PageTitle
        title="Apply Leave"
        subtitle="Create leave requests and track approval status"
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                From
              </Label>
              <DatePicker
                value={filterFromDate}
                onChange={setFilterFromDate}
                className="h-8 w-36 text-xs"
                testId="staff-leave-filter-from"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                To
              </Label>
              <DatePicker
                value={filterToDate}
                onChange={setFilterToDate}
                className="h-8 w-36 text-xs"
                testId="staff-leave-filter-to"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                Staff
              </Label>
              <Combobox
                value={filterStaffId}
                onChange={setFilterStaffId}
                options={leaveFilterStaffOptions}
                placeholder="All Staff"
                searchPlaceholder="Search staff"
                testid="staff-leave-filter-staff"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exportLeaveExcel}
              className="h-8"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            <Button
              size="sm"
              onClick={openCreate}
              className="h-8 erp-primary"
              disabled={!permissions.canAdd}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Apply Leave
            </Button>
          </div>
        }
      />
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-slate-100 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Staff</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Applied On</th>
              <th className="px-3 py-2 text-left">From</th>
              <th className="px-3 py-2 text-left">To</th>
              <th className="px-3 py-2 text-left">Attachment</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-slate-100 hover:bg-slate-50"
              >
                <td className="px-3 py-2">{staffNameById(row.staff_id)}</td>
                <td className="px-3 py-2">{row.type || "-"}</td>
                <td className="px-3 py-2">
                  {formatDisplayDate(row.created_at)}
                </td>
                <td className="px-3 py-2">
                  {formatDisplayDate(row.from_date)}
                </td>
                <td className="px-3 py-2">{formatDisplayDate(row.to_date)}</td>
                <td className="px-3 py-2">
                  {row.attachment_url ? (
                    <a
                      href={resolveAttachmentUrl(row.attachment_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                      title={row.attachment_name || "View attachment"}
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-3 py-2">{row.reason || "-"}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-semibold ${statusBadgeTone(row.status)}`}
                  >
                    {row.status || "pending"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      onClick={() => openEdit(row)}
                      disabled={!permissions.canEdit}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      onClick={() => openDelete(row)}
                      disabled={!permissions.canDelete}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Leave" : "Apply Leave"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Staff
              </Label>
              <Combobox
                value={form.staff_id || ""}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, staff_id: value }))
                }
                options={leaveStaffOptions}
                placeholder="Select staff"
                searchPlaceholder="Search staff by name or staff id"
                testid="staff-leave-select"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Leave Type
              </Label>
              <Combobox
                value={form.leave_type_id || ""}
                onChange={(value) =>
                  setForm((prev) => {
                    const picked = leaveTypeOptions.find(
                      (typeRow) => String(typeRow.id) === String(value || ""),
                    );
                    return {
                      ...prev,
                      leave_type_id: value,
                      type: picked?.name || prev.type || "",
                    };
                  })
                }
                options={leaveTypeSelectOptions}
                placeholder={
                  leaveTypeSelectOptions.length === 0
                    ? "No leave type configured"
                    : "Select leave type"
                }
                searchPlaceholder="Search leave type"
                testid="staff-leave-type-select"
              />
              {form.staff_id && selectedPendingLeaves.length > 0 && (
                <div className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                  Pending leave ({selectedPendingLeaves.length}) for selected
                  staff
                  {selectedTypeCode ? ` • ${selectedTypeLabel}` : ""}
                  <div className="mt-1 text-[10px]">
                    {selectedPendingLeaves
                      .slice(0, 2)
                      .map(
                        (row) =>
                          `${formatDisplayDate(row.from_date)} to ${formatDisplayDate(row.to_date)}`,
                      )
                      .join(" | ")}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                From Date
              </Label>
              <DatePicker
                value={form.from_date || ""}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, from_date: value }))
                }
                className="h-9 text-xs"
                testId="staff-leave-from"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                To Date
              </Label>
              <DatePicker
                value={form.to_date || ""}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, to_date: value }))
                }
                className="h-9 text-xs"
                testId="staff-leave-to"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Reason
              </Label>
              <Input
                value={form.reason || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, reason: e.target.value }))
                }
                className="h-9 text-xs"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] font-semibold text-slate-700 mb-1 block">
                Attachment
              </Label>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                className="h-9 text-xs"
              />
              {form.attachment_url && !attachmentFile && (
                <a
                  href={resolveAttachmentUrl(form.attachment_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                >
                  <Eye className="h-3.5 w-3.5" />
                  {form.attachment_name || "View existing attachment"}
                </a>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} className="erp-primary" disabled={uploading}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffActionDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
        title="Delete leave request?"
        description="This will permanently remove the selected leave request from the staff leave register."
        actionLabel="Delete Leave"
        onConfirm={remove}
      />
    </div>
  );
}

function StaffLeaveApprovalPage() {
  const permissions = useRouteModulePermissions("staff", "approve_leave");
  const { branchId, sessionId, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterStaffId, setFilterStaffId] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalRow, setApprovalRow] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("approved");
  const [approvalNote, setApprovalNote] = useState("");
  const [approving, setApproving] = useState(false);
  const canApprove = canApproveLeave(user?.role);

  const load = useCallback(async () => {
    const [staffRes, leaveRes] = await Promise.all([
      api.get("/staff", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      }),
      api.get("/leave-requests", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      }),
    ]);
    setStaff(staffRes.data.items || []);
    setRows(leaveRes.data.items || []);
  }, [branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const openApproval = (row, status) => {
    if (!permissions.canEdit) {
      toast.error("You do not have approval permission");
      return;
    }
    if (!canApprove) {
      toast.error("Only admin or superadmin can approve/reject leave");
      return;
    }
    if (normalizedLeaveStatus(row.status) !== "pending") {
      toast.error("Only pending requests can be updated");
      return;
    }
    setApprovalRow(row);
    setApprovalStatus(status);
    setApprovalNote(row.approver_note || "");
    setApprovalOpen(true);
  };

  const submitApproval = async () => {
    if (!approvalRow?.id) return;
    if (approvalStatus === "rejected" && !String(approvalNote || "").trim()) {
      toast.error("Rejection note is required");
      return;
    }
    try {
      setApproving(true);
      await api.put(`/leave-requests/${approvalRow.id}`, {
        ...approvalRow,
        branch_id: approvalRow.branch_id || branchId,
        session_id: approvalRow.session_id || sessionId,
        status: approvalStatus,
        approver_note: String(approvalNote || "").trim(),
      });
      toast.success(`Leave ${approvalStatus}`);
      setApprovalOpen(false);
      setApprovalRow(null);
      setApprovalNote("");
      load();
    } finally {
      setApproving(false);
    }
  };

  const filtered = rows.filter((row) => {
    if (filterStatus && normalizedLeaveStatus(row.status) !== filterStatus) {
      return false;
    }
    if (filterStaffId && String(row.staff_id || "") !== String(filterStaffId)) {
      return false;
    }
    const rowFrom = formatIsoDate(row.from_date);
    const rowTo = formatIsoDate(row.to_date || row.from_date);
    if (filterFromDate && rowTo < filterFromDate) return false;
    if (filterToDate && rowFrom > filterToDate) return false;
    return true;
  });
  const staffNameById = (id) =>
    staffDisplayName(staff.find((row) => String(row.id) === String(id)) || {});
  const approvalStaffFilterOptions = [
    { value: "", label: "All Staff", sub: "" },
    ...staff.map((row) => ({
      value: String(row.id),
      label: staffDisplayName(row),
      sub: row.staff_id || row.email || "",
    })),
  ];
  const approvalStatusOptions = [
    { value: "pending", label: "Pending", sub: "" },
    { value: "approved", label: "Approved", sub: "" },
    { value: "rejected", label: "Rejected", sub: "" },
    { value: "", label: "All", sub: "" },
  ];

  const exportApprovalExcel = () => {
    if (filtered.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    const columns = [
      { key: "staff", label: "Staff" },
      { key: "type", label: "Type" },
      { key: "applied_on", label: "Applied On" },
      { key: "from", label: "From" },
      { key: "to", label: "To" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status" },
      { key: "approver_note", label: "Approver Note" },
    ];
    const exportRows = filtered.map((row) => ({
      staff: staffNameById(row.staff_id),
      type: row.type || "-",
      applied_on: formatDisplayDate(row.created_at),
      from: formatDisplayDate(row.from_date),
      to: formatDisplayDate(row.to_date),
      reason: row.reason || "",
      status: row.status || "pending",
      approver_note: row.approver_note || "",
    }));
    downloadCSV(exportRows, columns, "staff_approve_leave.csv");
    toast.success(`Exported ${exportRows.length} rows`);
  };

  return (
    <div data-testid="staff-approve-leave-page">
      <PageTitle
        title="Approve Leave"
        subtitle="Review pending requests and update approval status"
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                From
              </Label>
              <DatePicker
                value={filterFromDate}
                onChange={setFilterFromDate}
                className="h-8 w-36 text-xs"
                testId="staff-approve-filter-from"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                To
              </Label>
              <DatePicker
                value={filterToDate}
                onChange={setFilterToDate}
                className="h-8 w-36 text-xs"
                testId="staff-approve-filter-to"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                Staff
              </Label>
              <Combobox
                value={filterStaffId}
                onChange={setFilterStaffId}
                options={approvalStaffFilterOptions}
                placeholder="All Staff"
                searchPlaceholder="Search staff"
                testid="staff-approve-filter-staff"
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                Status
              </Label>
              <Combobox
                value={filterStatus}
                onChange={setFilterStatus}
                options={approvalStatusOptions}
                placeholder="Status"
                searchPlaceholder="Search status"
                testid="staff-approve-filter-status"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exportApprovalExcel}
              className="h-8"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
          </div>
        }
      />
      {!canApprove && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Approval actions are restricted to admin or superadmin users.
        </div>
      )}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-slate-100 text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Staff</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Applied On</th>
              <th className="px-3 py-2 text-left">From</th>
              <th className="px-3 py-2 text-left">To</th>
              <th className="px-3 py-2 text-left">Attachment</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Approval</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr
                key={row.id}
                className="border-b border-slate-100 hover:bg-slate-50"
              >
                <td className="px-3 py-2">{staffNameById(row.staff_id)}</td>
                <td className="px-3 py-2">{row.type || "-"}</td>
                <td className="px-3 py-2">
                  {formatDisplayDate(row.created_at)}
                </td>
                <td className="px-3 py-2">
                  {formatDisplayDate(row.from_date)}
                </td>
                <td className="px-3 py-2">{formatDisplayDate(row.to_date)}</td>
                <td className="px-3 py-2">
                  {row.attachment_url ? (
                    <a
                      href={resolveAttachmentUrl(row.attachment_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                      title={row.attachment_name || "View attachment"}
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-3 py-2">{row.reason || "-"}</td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-semibold ${statusBadgeTone(row.status)}`}
                  >
                    {row.status || "pending"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="h-7 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => openApproval(row, "approved")}
                      disabled={
                        !canApprove ||
                        normalizedLeaveStatus(row.status) !== "pending"
                      }
                    >
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-rose-700 border-rose-200"
                      onClick={() => openApproval(row, "rejected")}
                      disabled={
                        !canApprove ||
                        normalizedLeaveStatus(row.status) !== "pending"
                      }
                    >
                      <XCircle className="mr-1 h-3.5 w-3.5" />
                      Reject
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {approvalStatus === "approved" ? "Approve Leave" : "Reject Leave"}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              {approvalStatus === "approved"
                ? "Approval note"
                : "Rejection note *"}
            </Label>
            <textarea
              rows={3}
              className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              placeholder={
                approvalStatus === "approved"
                  ? "Enter approval note"
                  : "Enter rejection reason"
              }
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setApprovalOpen(false);
                setApprovalRow(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitApproval}
              disabled={approving}
              className={
                approvalStatus === "approved"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-rose-600 hover:bg-rose-700 text-white"
              }
            >
              {approving
                ? "Saving..."
                : approvalStatus === "approved"
                  ? "Approve"
                  : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StaffLeaveStatusReport() {
  const { branchId, sessionId } = useAuth();
  const [staffRows, setStaffRows] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [staffId, setStaffId] = useState("");

  const load = useCallback(async () => {
    const [staffRes, typeRes, leaveRes] = await Promise.all([
      api.get("/staff", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      }),
      api.get("/leave-types", {
        params: { branch_id: branchId, session_id: sessionId, limit: 200 },
      }),
      api.get("/leave-requests", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      }),
    ]);
    const staffItems = staffRes.data.items || [];
    setStaffRows(staffItems);
    setLeaveTypes(typeRes.data.items || []);
    setRequests(leaveRes.data.items || []);
    if (!staffId && staffItems[0]?.id) {
      setStaffId(String(staffItems[0].id));
    }
  }, [branchId, sessionId, staffId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredStaff = staffRows.filter((row) =>
    roleFilter ? String(row.role || "") === roleFilter : true,
  );
  const selected =
    filteredStaff.find((row) => String(row.id) === String(staffId)) ||
    filteredStaff[0] ||
    null;

  const typeRows = leaveTypes.length > 0 ? leaveTypes : [];

  const rows = selected
    ? typeRows.map((typeRow) => {
        const typeName = typeRow.name || typeRow.type || "Leave";
        const code = leaveTypeCode(typeName);
        const staffAllotted = Number(selected[`leaves_${code}`] || 0);
        const fallbackAllotted = Number(typeRow.max_per_year || 0);
        const allotted = staffAllotted > 0 ? staffAllotted : fallbackAllotted;

        const staffTypeRequests = requests.filter(
          (request) =>
            String(request.staff_id || "") === String(selected.id) &&
            leaveTypeMatches(request, typeName, typeRow.id),
        );
        const approved = staffTypeRequests.filter(
          (request) => normalizedLeaveStatus(request.status) === "approved",
        ).length;
        const pending = staffTypeRequests.filter(
          (request) => normalizedLeaveStatus(request.status) === "pending",
        ).length;
        const rejected = staffTypeRequests.filter(
          (request) => normalizedLeaveStatus(request.status) === "rejected",
        ).length;
        const available = Math.max(allotted - approved - pending, 0);

        return {
          leave_type: typeName,
          allotted,
          approved,
          pending,
          rejected,
          available,
        };
      })
    : [];

  const totals = rows.reduce(
    (acc, row) => {
      acc.allotted += row.allotted;
      acc.approved += row.approved;
      acc.pending += row.pending;
      acc.rejected += row.rejected;
      acc.available += row.available;
      return acc;
    },
    { allotted: 0, approved: 0, pending: 0, rejected: 0, available: 0 },
  );

  const roles = Array.from(
    new Set(staffRows.map((row) => String(row.role || "")).filter(Boolean)),
  );
  const roleOptions = [
    { value: "", label: "All", sub: "" },
    ...roles.map((role) => ({ value: role, label: role, sub: "" })),
  ];
  const staffOptions = filteredStaff.map((row) => ({
    value: String(row.id),
    label: staffDisplayName(row),
    sub: `${row.role || "-"} • ${row.staff_id || "-"}`,
  }));

  const exportReport = () => {
    if (!selected || rows.length === 0) {
      toast.info("No leave report rows to export");
      return;
    }
    downloadCSV(
      rows,
      [
        { key: "leave_type", label: "Leave Type" },
        { key: "allotted", label: "Allotted" },
        { key: "approved", label: "Approved" },
        { key: "pending", label: "Pending" },
        { key: "rejected", label: "Rejected" },
        { key: "available", label: "Available" },
      ],
      `leave-status-${staffDisplayName(selected).replace(/\s+/g, "-").toLowerCase()}.csv`,
    );
  };

  return (
    <div data-testid="staff-leave-status-report">
      <PageTitle
        title="Leave Status Report"
        subtitle="Track allotted, approved, pending and available leaves by staff"
      />
      <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <Label className="mb-1 block text-xs font-semibold text-slate-700">
              Role
            </Label>
            <Combobox
              value={roleFilter}
              onChange={(value) => {
                setRoleFilter(value);
                setStaffId("");
              }}
              options={roleOptions}
              placeholder="All"
              searchPlaceholder="Search role"
              testid="leave-status-role"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="mb-1 block text-xs font-semibold text-slate-700">
              Staff
            </Label>
            <Combobox
              value={staffId}
              onChange={setStaffId}
              options={staffOptions}
              placeholder="Select staff"
              searchPlaceholder="Search staff"
              testid="leave-status-staff"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              className="h-9 w-full"
              onClick={exportReport}
              disabled={!selected || rows.length === 0}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-5">
        {[
          ["Allotted", totals.allotted, "text-slate-800"],
          ["Approved", totals.approved, "text-emerald-700"],
          ["Pending", totals.pending, "text-amber-700"],
          ["Rejected", totals.rejected, "text-rose-700"],
          ["Available", totals.available, "text-blue-700"],
        ].map(([label, value, tone]) => (
          <div
            key={label}
            className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
          >
            <div className="text-xs font-semibold uppercase text-slate-500">
              {label}
            </div>
            <div className={`mt-1 text-xl font-bold ${tone}`}>{value}</div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-slate-100 text-xs uppercase text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">Leave Type</th>
              <th className="px-3 py-2 text-left">Allotted</th>
              <th className="px-3 py-2 text-left">Approved</th>
              <th className="px-3 py-2 text-left">Pending</th>
              <th className="px-3 py-2 text-left">Rejected</th>
              <th className="px-3 py-2 text-left">Available</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.leave_type} className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium">{row.leave_type}</td>
                <td className="px-3 py-2">{row.allotted}</td>
                <td className="px-3 py-2 text-emerald-700 font-semibold">
                  {row.approved}
                </td>
                <td className="px-3 py-2 text-amber-700 font-semibold">
                  {row.pending}
                </td>
                <td className="px-3 py-2 text-rose-700 font-semibold">
                  {row.rejected}
                </td>
                <td className="px-3 py-2 text-blue-700 font-semibold">
                  {row.available}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={6}
                >
                  No leave status data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeacherRatingsPage() {
  const permissions = useRouteModulePermissions("staff", "teacher_ratings");
  const [rows, setRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    staff_id: "",
    rating: "",
    month: "",
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ratingsRes, staffRes] = await Promise.all([
        api.get("/teachers-ratings", { params: { limit: 1000 } }),
        api.get("/staff", { params: { limit: 2000 } }),
      ]);
      setRows(ratingsRes.data.items || []);
      setStaffRows(staffRes.data.items || []);
    } catch {
      toast.error("Failed to load teacher ratings");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const teacherRows = staffRows.filter((row) => {
    const role = String(row.role || "")
      .trim()
      .toLowerCase();
    const teacherType = String(row.teacher_type || "")
      .trim()
      .toLowerCase();
    return role === "teacher" || teacherType.includes("teach");
  });

  const teacherOptions = teacherRows
    .map((row) => ({
      value: String(row.id),
      label: staffDisplayName(row),
      sub: `${row.staff_id || ""}${row.teacher_type ? ` | ${row.teacher_type}` : ""}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const teacherNameById = (id) => {
    const row = teacherRows.find((r) => String(r.id) === String(id));
    return row ? staffDisplayName(row) : "-";
  };

  const filteredRows = rows.filter((row) => {
    const q = search.trim().toLowerCase();
    const month = String(row.rated_on || "").slice(0, 7);
    const teacherName = teacherNameById(row.staff_id).toLowerCase();
    const notes = String(row.notes || "").toLowerCase();

    if (monthFilter && month !== monthFilter) return false;
    if (!q) return true;
    return (
      teacherName.includes(q) ||
      String(row.staff_id || "").includes(q) ||
      String(row.rating || "").includes(q) ||
      notes.includes(q)
    );
  });

  const openCreate = () => {
    setEditing(null);
    const todayMonth = new Date().toISOString().slice(0, 7);
    setForm({ staff_id: "", rating: "", month: todayMonth, notes: "" });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      staff_id: String(row.staff_id || ""),
      rating: String(row.rating || ""),
      month: String(row.rated_on || "").slice(0, 7),
      notes: String(row.notes || ""),
    });
    setOpen(true);
  };

  const save = async () => {
    if (editing && !permissions.canEdit) {
      toast.error("You do not have edit permission for teacher ratings");
      return;
    }
    if (!editing && !permissions.canAdd) {
      toast.error("You do not have add permission for teacher ratings");
      return;
    }
    if (saving) return;
    if (!form.staff_id) {
      toast.error("Teacher is required");
      return;
    }
    const rating = Number(form.rating || 0);
    if (!Number.isFinite(rating) || rating < 1 || rating > 5) {
      toast.error("Rating must be between 1 and 5");
      return;
    }
    if (!form.month || !/^\d{4}-\d{2}$/.test(form.month)) {
      toast.error("Month is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        staff_id: Number(form.staff_id),
        rating,
        rated_on: `${form.month}-01`,
        notes: form.notes || null,
      };

      if (editing?.id) {
        await api.put(`/teachers-ratings/${editing.id}`, payload);
        toast.success("Teacher rating updated");
      } else {
        await api.post("/teachers-ratings", payload);
        toast.success("Teacher rating added");
      }

      setOpen(false);
      await load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteRow?.id) return;
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for teacher ratings");
      return;
    }
    try {
      await api.delete(`/teachers-ratings/${deleteRow.id}`);
      toast.success("Teacher rating deleted");
      setDeleteRow(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportRows = () => {
    if (filteredRows.length === 0) {
      toast.info("No ratings to export");
      return;
    }
    const sheet = filteredRows.map((row) => ({
      teacher: teacherNameById(row.staff_id),
      teacher_id: row.staff_id,
      rating: row.rating,
      month: String(row.rated_on || "").slice(0, 7),
      feedback: row.notes || "",
    }));
    downloadCSV(
      sheet,
      [
        { key: "teacher", label: "Teacher" },
        { key: "teacher_id", label: "Teacher ID" },
        { key: "rating", label: "Rating" },
        { key: "month", label: "Month" },
        { key: "feedback", label: "Feedback" },
      ],
      "teachers_rating.csv",
    );
    toast.success("Teacher ratings exported");
  };

  return (
    <div data-testid="teachers-rating-page" className="space-y-3">
      <PageTitle
        title="Teachers Rating"
        subtitle="Teacher-wise monthly rating with feedback"
        actions={
          <>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5 block">
                  Month
                </Label>
                <Input
                  type="month"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="h-9 w-36"
                  data-testid="ratings-filter-month"
                />
              </div>
              {monthFilter && (
                <Button
                  variant="ghost"
                  className="h-9 px-2"
                  onClick={() => setMonthFilter("")}
                >
                  Clear
                </Button>
              )}
            </div>
            <Input
              placeholder="Search teacher / feedback"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-56"
              data-testid="ratings-search"
            />
            <Button
              variant="outline"
              className="h-9"
              onClick={exportRows}
              data-testid="ratings-export"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Export
            </Button>
            {permissions.canAdd && (
              <Button
                className="h-9 erp-primary"
                onClick={openCreate}
                data-testid="ratings-add"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add New
              </Button>
            )}
          </>
        }
      />

      <DataTable
        testid="ratings-table"
        columns={[
          {
            key: "staff_id",
            label: "Teacher",
            render: (row) => {
              const t = teacherRows.find(
                (item) => String(item.id) === String(row.staff_id),
              );
              return (
                <div>
                  <div className="font-medium">
                    {teacherNameById(row.staff_id)}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {t?.staff_id || ""}
                  </div>
                </div>
              );
            },
          },
          { key: "rating", label: "Rating" },
          {
            key: "rated_on",
            label: "Month",
            render: (row) => String(row.rated_on || "").slice(0, 7) || "-",
          },
          { key: "notes", label: "Feedback" },
        ]}
        rows={filteredRows}
        onEdit={permissions.canEdit ? openEdit : undefined}
        onDelete={permissions.canDelete ? setDeleteRow : undefined}
        emptyLabel={
          loading ? "Loading teacher ratings..." : "No teacher ratings yet"
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : "Add New"} · Teachers Rating
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Teacher *</Label>
              <Combobox
                value={form.staff_id}
                onChange={(value) => setForm({ ...form, staff_id: value })}
                options={teacherOptions}
                placeholder="Select teacher"
                testid="ratings-form-teacher"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Rating (1-5) *</Label>
              <Input
                type="number"
                min={1}
                max={5}
                step={0.1}
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: e.target.value })}
                className="h-9"
                data-testid="ratings-form-rating"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Month *</Label>
              <Input
                type="month"
                value={form.month}
                onChange={(e) => setForm({ ...form, month: e.target.value })}
                className="h-9"
                data-testid="ratings-form-month"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Feedback</Label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
                data-testid="ratings-form-feedback"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="erp-primary"
              disabled={saving}
              data-testid="ratings-form-save"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete teacher rating?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This will remove selected teacher rating entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Staff() {
  const directoryPermissions = useRouteModulePermissions("staff", "directory");
  const attendancePermissions = useRouteModulePermissions(
    "staff",
    "attendance",
  );
  const payrollPermissions = useRouteModulePermissions("staff", "payroll");
  const leavePermissions = useRouteModulePermissions("staff", "leave_requests");
  const approveLeavePermissions = useRouteModulePermissions(
    "staff",
    "approve_leave",
  );
  const leaveTypesPermissions = useRouteModulePermissions(
    "staff",
    "leave_types",
  );
  const leaveStatsPermissions = useRouteModulePermissions(
    "staff",
    "leave_stats",
  );
  const ratingsPermissions = useRouteModulePermissions(
    "staff",
    "teacher_ratings",
  );
  const departmentsPermissions = useRouteModulePermissions(
    "staff",
    "departments",
  );
  const designationsPermissions = useRouteModulePermissions(
    "staff",
    "designations",
  );
  const items = [
    directoryPermissions.canView && {
      to: "/staff/directory",
      label: "Directory",
    },
    attendancePermissions.canView && {
      to: "/staff/attendance",
      label: "Attendance",
    },
    payrollPermissions.canView && { to: "/staff/payroll", label: "Payroll" },
    leavePermissions.canView && {
      to: "/staff/apply-leave",
      label: "Apply Leave",
    },
    approveLeavePermissions.canView && {
      to: "/staff/approve-leave",
      label: "Approve Leave",
    },
    leaveTypesPermissions.canView && {
      to: "/staff/leave-types",
      label: "Leave Types",
    },
    leaveStatsPermissions.canView && {
      to: "/staff/leave-stats",
      label: "Leave Stats",
    },
    ratingsPermissions.canView && {
      to: "/staff/ratings",
      label: "Teachers Rating",
    },
    departmentsPermissions.canView && {
      to: "/staff/departments",
      label: "Departments",
    },
    designationsPermissions.canView && {
      to: "/staff/designations",
      label: "Designations",
    },
  ].filter(Boolean);
  if (items.length === 0) {
    return (
      <div data-testid="staff-page">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
          <div className="text-base font-semibold text-slate-700">
            No staff workflow assigned
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Ask super admin to enable Staff sub-features for this role.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="staff-page">
      <SubNav items={items} />
      <Routes>
        <Route index element={<Navigate to={items[0].to} replace />} />
        {directoryPermissions.canView && (
          <Route path="directory" element={<StaffDirectory />} />
        )}
        {attendancePermissions.canView && (
          <Route path="attendance" element={<StaffAttendance />} />
        )}
        {payrollPermissions.canView && (
          <Route path="payroll" element={<StaffPayrollPage />} />
        )}
        {leavePermissions.canView && (
          <Route path="apply-leave" element={<StaffLeavePage />} />
        )}
        {approveLeavePermissions.canView && (
          <Route path="approve-leave" element={<StaffLeaveApprovalPage />} />
        )}
        {leaveTypesPermissions.canView && (
          <Route
            path="leave-types"
            element={
              <MasterCrudPage
                title="Leave Types"
                endpoint="/leave-types"
                testid="leave-types"
                moduleKey="staff"
                submoduleKey="leave_types"
                fields={[
                  { key: "name", label: "Name *", required: true },
                  {
                    key: "max_per_year",
                    label: "Max per year",
                    type: "number",
                  },
                  {
                    key: "is_paid",
                    label: "Paid?",
                    type: "select",
                    options: [
                      { value: "1", label: "Yes" },
                      { value: "0", label: "No" },
                    ],
                  },
                ]}
                columns={[
                  { key: "name", label: "Name" },
                  { key: "max_per_year", label: "Max per year" },
                  {
                    key: "is_paid",
                    label: "Paid",
                    render: (row) =>
                      String(row.is_paid || "0") === "1" ? "Yes" : "No",
                  },
                ]}
              />
            }
          />
        )}
        {leaveStatsPermissions.canView && (
          <Route path="leave-stats" element={<StaffLeaveStatusReport />} />
        )}
        {ratingsPermissions.canView && (
          <Route path="ratings" element={<TeacherRatingsPage />} />
        )}
        {departmentsPermissions.canView && (
          <Route
            path="departments"
            element={
              <MasterCrudPage
                title="Departments"
                endpoint="/departments"
                testid="departments"
                moduleKey="staff"
                submoduleKey="departments"
                fields={[{ key: "name", label: "Name *", required: true }]}
              />
            }
          />
        )}
        {designationsPermissions.canView && (
          <Route
            path="designations"
            element={
              <MasterCrudPage
                title="Designations"
                endpoint="/designations"
                testid="designations"
                moduleKey="staff"
                submoduleKey="designations"
                fields={[{ key: "name", label: "Name *", required: true }]}
              />
            }
          />
        )}
      </Routes>
    </div>
  );
}
